import React, { createContext, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

const ThemeContext = createContext();

/**
 * ThemeProvider - Aplikasyonun tümünde dark/light mode'u yönetir
 *
 * Özellikleri:
 * 1. localStorage'dan tema tercihini oku ve persist et
 * 2. DOM'a dark class'ı ekle/çıkar
 * 3. Route değişikliklerinde localStorage'dan recheck et
 * 4. Diğer tab/window'lardan localStorage değişikliklerini dinle
 */
export const ThemeProvider = ({ children }) => {
  // 1. localStorage'dan tema tercihini oku
  const readThemeFromStorage = () => {
    try {
      const savedTheme = localStorage.getItem('theme');

      if (savedTheme === 'dark' || savedTheme === 'light') {
        return savedTheme === 'dark';
      }

      // Fallback: sistem preferences
      const isSystemDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      return isSystemDark;
    } catch (error) {
      return false;
    }
  };

  // 2. useState'i initialize et
  const [isDark, setIsDark] = useState(() => {
    return readThemeFromStorage();
  });

  // 3. isDark değişince, localStorage'a kaydet ve DOM'u güncelle
  useEffect(() => {
    // localStorage'a kaydet
    const themeValue = isDark ? 'dark' : 'light';
    localStorage.setItem('theme', themeValue);

    // DOM'u güncelle
    const htmlElement = document.documentElement;
    if (isDark) {
      htmlElement.classList.add('dark');
    } else {
      htmlElement.classList.remove('dark');
    }
  }, [isDark]);

  // 4. Mount olur olmaz, localStorage'dan tekrar oku (race condition'u düzelt)
  useEffect(() => {
    const savedTheme = readThemeFromStorage();
    if (savedTheme !== isDark) {
      setIsDark(savedTheme);
    }
  }, []);

  // 5. Route değiştiğinde localStorage'dan recheck et (kritik!)
  const location = useLocation();
  useEffect(() => {
    const savedTheme = readThemeFromStorage();
    if (savedTheme !== isDark) {
      setIsDark(savedTheme);
    }
  }, [location.pathname]);

  // 6. Diğer tab/window'lardan localStorage değişikliklerini dinle
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'theme') {
        const newTheme = e.newValue === 'dark';
        setIsDark(newTheme);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const toggleTheme = () => {
    setIsDark(prev => !prev);
  };

  return (
    <ThemeContext.Provider value={{ isDark, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
